// Tile-media lazy loader + play-state detection.
//
// YouTube/Vimeo iframes ship with data-src only; we swap it to src when
// the tile scrolls into view so pages with many tiles don't load every
// embed up front. The iframe also starts with opacity 0 (via CSS) and is
// faded in only once the embed reports it's actually playing — so viewers
// see the poster image during the player's loading/setup phase instead
// of the YouTube or Vimeo branded loading screen.
//
// Exposes window.hahnTileMedia.refresh() so views that re-render their tile
// grid (e.g. the Our Work filter in work-page.js, which detaches and
// re-inserts tiles) can re-arm lazy-loading for the iframes they move. The
// original IntersectionObserver does not reliably re-fire for a target that's
// detached and re-attached into a new parent, so without this hook a tile
// surfaced by filtering would keep its data-src and never load.

(function () {
  const SELECTOR = 'iframe[data-tile-video][data-src]';

  // Delay before revealing the iframe after the embed reports PLAYING.
  // YouTube briefly shows its title / play-button overlay for roughly one
  // second after playback starts, then auto-dismisses it. Holding the
  // poster image for a beat longer lets that chrome disappear off-screen.
  const REVEAL_DELAY_MS = {
    youtube: 1200,
    vimeo: 300
  };

  const markPlaying = (iframe) => {
    const tile = iframe.closest('.tile-media');
    if (!tile || tile.classList.contains('tile-media--playing')) return;
    const type = iframe.getAttribute('data-tile-video');
    const delay = REVEAL_DELAY_MS[type] || 0;
    window.setTimeout(() => tile.classList.add('tile-media--playing'), delay);
  };

  const subscribe = (iframe) => {
    const type = iframe.getAttribute('data-tile-video');
    const win = iframe.contentWindow;
    if (!win) return;

    if (type === 'youtube') {
      // YouTube iframe API postMessage protocol (requires enablejsapi=1).
      win.postMessage('{"event":"listening"}', '*');
      win.postMessage(
        JSON.stringify({
          event: 'command',
          func: 'addEventListener',
          args: ['onStateChange']
        }),
        '*'
      );
    } else if (type === 'vimeo') {
      // Vimeo Player API postMessage protocol.
      win.postMessage(JSON.stringify({ method: 'addEventListener', value: 'play' }), '*');
      win.postMessage(JSON.stringify({ method: 'addEventListener', value: 'playing' }), '*');
    }
  };

  const activate = (iframe) => {
    const src = iframe.getAttribute('data-src');
    if (!src) return;
    iframe.addEventListener('load', () => subscribe(iframe), { once: true });
    iframe.setAttribute('src', src);
    iframe.removeAttribute('data-src');
  };

  // Global listener for messages from any tile iframe. Queried live so it
  // still matches tiles after the grid re-renders.
  window.addEventListener('message', (event) => {
    let data = event.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        return;
      }
    }
    if (!data || typeof data !== 'object') return;

    // YouTube: { event: "onStateChange", info: 1 } where 1 = PLAYING.
    // Vimeo:   { event: "play" } or { event: "playing" }.
    const isPlaying =
      (data.event === 'onStateChange' && data.info === 1) ||
      data.event === 'play' ||
      data.event === 'playing';
    if (!isPlaying) return;

    document.querySelectorAll('iframe[data-tile-video]').forEach((iframe) => {
      if (iframe.contentWindow === event.source) markPlaying(iframe);
    });
  });

  // Without IntersectionObserver support, activate every pending embed
  // immediately — both now and whenever a re-rendered grid asks to refresh.
  if (!('IntersectionObserver' in window)) {
    const activateAll = () => document.querySelectorAll(SELECTOR).forEach(activate);
    window.hahnTileMedia = { refresh: activateAll };
    activateAll();
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          activate(entry.target);
          observer.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '200px 0px' }
  );

  // (Re)observe every embed that still needs loading. Re-observing is done as
  // unobserve + observe so the observer re-evaluates intersection for tiles
  // that were detached and re-inserted (a plain observe() on an already-
  // observed target is a no-op and wouldn't re-fire). Already-activated tiles
  // have had data-src removed, so they no longer match SELECTOR and are skipped.
  const observeAll = () => {
    document.querySelectorAll(SELECTOR).forEach((iframe) => {
      observer.unobserve(iframe);
      observer.observe(iframe);
    });
  };

  window.hahnTileMedia = { refresh: observeAll };

  observeAll();
})();
